import { useId } from 'react';
import { seriesVar, MARK } from './chartTokens';
import { percent } from '../../lib/format';
import './charts.css';

/**
 * Before/after ownership for a modelled round.
 *
 * Form choice: comparison of one measure across entities, twice. Paired
 * horizontal bars, because stakeholder names are text of varying length and
 * horizontal bars give them room without rotated labels.
 *
 * Both bars share one scale. The "after" bar carries the series hue; "before" is
 * a recessive outline, so the eye reads the *change* rather than two competing
 * categories.
 */
export default function DilutionBars({ rows, limit = 8 }) {
  const titleId = useId();
  const visible = (rows ?? []).slice(0, limit);

  if (!visible.length) {
    return <div className="chart-empty">Nothing to compare yet.</div>;
  }

  const max = Math.max(...visible.flatMap((r) => [Number(r.before), Number(r.after)]), 1);
  const rowH = 40;
  const barH = 12;
  const labelW = 150;
  const valueW = 132;
  const width = 640;
  const plotW = width - labelW - valueW;
  const height = visible.length * rowH + 12;

  return (
    <div className="chart-block">
      <svg
        className="barchart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>Ownership before and after the modelled round</title>
        {visible.map((row, index) => {
          const y = index * rowH + 8;
          const beforeW = (Number(row.before) / max) * plotW;
          const afterW = (Number(row.after) / max) * plotW;
          const diluted = Number(row.after) < Number(row.before);
          return (
            <g key={row.stakeholderId}>
              <text x={0} y={y + barH + 4} className="bar-label">
                {row.name.length > 20 ? `${row.name.slice(0, 19)}…` : row.name}
              </text>

              {/* Before: recessive outline anchored to the baseline */}
              <rect
                x={labelW}
                y={y}
                width={Math.max(beforeW, 1)}
                height={barH}
                rx={MARK.cornerRadius}
                className="bar-before"
              />

              {/* After: the actual value, 2px surface gap below the before bar */}
              <rect
                x={labelW}
                y={y + barH + MARK.surfaceGap}
                width={Math.max(afterW, 1)}
                height={barH}
                rx={MARK.cornerRadius}
                fill={seriesVar(index)}
              />

              {/* Direct label — every row, because this chart has few rows and
                  the delta is the point. */}
              <text x={labelW + plotW + 10} y={y + barH + 4} className="bar-value num">
                {percent(row.before)} → {percent(row.after)}
              </text>
              <text
                x={width - 6}
                y={y + barH + 4}
                className={diluted ? 'delta-down' : 'delta-flat'}
                textAnchor="end"
              >
                {diluted ? '▼' : '—'}
              </text>
            </g>
          );
        })}
      </svg>

      <ul className="legend-row">
        <li>
          <span className="swatch swatch-outline" aria-hidden="true" /> Before round
        </li>
        <li>
          <span className="swatch" style={{ background: seriesVar(0) }} aria-hidden="true" /> After round
        </li>
      </ul>
    </div>
  );
}
